import AppSettings from 'src/app/common/app.settings';
import ProjectLinks from 'src/app/common/project.links';
import FieldAllowedAttributeTypes from 'src/app/forms/fields/field-allowed-attribute-types.values';
import FieldAllowedValueClasses from 'src/app/forms/fields/field-allowed-value-classes.values';
import { FormTypes } from 'src/app/forms/forms.enum';
import { Attribute } from 'src/app/interfaces/form-and-field.interface';
import {
  TableCellOnActionEvent,
  TableConstructorData,
  UserTableConstructorInfo,
} from 'src/app/interfaces/tables.interfaces';
import { ItemsListContent } from 'src/app/interfaces/views.interface';
import {
  AccountStatus,
  Communities,
  Community,
  CommunityPermissions,
  CommunityStatus,
  HealthCommunity,
  ListOfBasicUsers,
  PendingUserInvolvementInCommunity,
  UserRelationToGroup,
} from 'src/app/swagger-api';
import { ColumnTypes } from 'src/app/views/grid-user-list/grid-table.enum';
import { tableComponentStyles } from 'src/app/views/table-users-list/adapters/table.styles';
import {
  manageCommunityTableHead,
  userInvitesTableCellAttributes,
} from 'src/app/views/table-users-list/table-users-list.data';
import { mockUser } from '../profile/profile.mock';
import { CellActionTypes } from '../tables/components/cell-actions.type';
import { CommunityView } from './communities-store';

export const mockMembers: ListOfBasicUsers = {
  items: [
    {
      user_id: 'userid',
      displayed_name: 'StiveJobs',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid1',
      displayed_name: 'Morty',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid2',
      displayed_name: 'StiveJobs1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid3',
      displayed_name: 'ArnoldShwartz',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid4',
      displayed_name: 'AntonyHopkins',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid5',
      displayed_name: 'MarkTwen',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid6',
      displayed_name: 'SantaClous',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid7',
      displayed_name: 'Patric',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid8',
      displayed_name: 'AgentSmith',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid9',
      displayed_name: 'Trinity',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
  ],
};

export const communityLeadPermissions: CommunityPermissions = {
  view: true,
  update: true,
  add_leader: true,
  remove_leader: true,
  remove: true,
  invite_users: true,
  view_invitations: true,
  leave: false,
  request_to_join: false,
  view_join_requests: true,
  accept_join_request: true,
  deny_join_request: true,
  request_to_link: true,
  accept_link_request: true,
  deny_link_request: true,
  unlink: true,
  view_members: true,
  remove_member: true,
  view_projects: true,
  create_project: true,
  remove_project: true,
  view_posts: true,
  add_post: true,
};

export const communityMemberPermissions: CommunityPermissions = {
  view: true,
  update: false,
  add_leader: false,
  remove_leader: false,
  remove: false,
  invite_users: true,
  view_invitations: false,
  leave: true,
  request_to_join: false,
  accept_join_request: false,
  view_join_requests: false,
  deny_join_request: false,
  request_to_link: false,
  accept_link_request: false,
  deny_link_request: false,
  unlink: false,
  view_members: true,
  remove_member: false,
  view_projects: true,
  create_project: true,
  remove_project: false,
  view_posts: true,
  add_post: true,
};

export const communityRegularUserPermissions: CommunityPermissions = {
  view: true,
  update: false,
  add_leader: false,
  remove_leader: false,
  remove: false,
  invite_users: false,
  view_invitations: false,
  leave: false,
  request_to_join: true,
  view_join_requests: false,
  accept_join_request: false,
  deny_join_request: false,
  request_to_link: false,
  accept_link_request: false,
  deny_link_request: false,
  unlink: false,
  view_members: true,
  remove_member: false,
  view_projects: true,
  create_project: false,
  remove_project: false,
  view_posts: true,
  add_post: false,
};

export const communityNoPermissions: CommunityPermissions = {
  view: false,
  update: false,
  add_leader: false,
  remove_leader: false,
  remove: false,
  invite_users: false,
  view_invitations: false,
  leave: false,
  request_to_join: false,
  view_join_requests: false,
  accept_join_request: false,
  deny_join_request: false,
  request_to_link: false,
  accept_link_request: false,
  deny_link_request: false,
  unlink: false,
  view_members: false,
  remove_member: false,
  view_projects: false,
  create_project: false,
  remove_project: false,
  view_posts: false,
  add_post: false,
};

export const pagination: { offset: number; limit: number } = { offset: 0, limit: 10 };

export function mockTableCellOnActionEvent(data: {
  type: CellActionTypes;
  entityId: string;
  actorId?: string;
  cellPosition?: { rowIndex: number; cellIndex: number };
}): TableCellOnActionEvent {
  return {
    type: data.type,
    entityId: data.entityId,
    actorId: 'actorId',
    cellPosition: { rowIndex: 1, cellIndex: 1 },
  };
}

export function mockCommunity(data?: {
  permissions?: CommunityPermissions;
  entityId?: string;
  status?: CommunityStatus;
  actor_role?: UserRelationToGroup;
}): Community {
  const permissions: CommunityPermissions = data?.permissions ?? communityRegularUserPermissions;
  const entityId: string = data?.entityId ?? 'sdgageg';
  const status: CommunityStatus = data?.status ?? CommunityStatus.Open;
  const actorRole: UserRelationToGroup = data?.actor_role ?? UserRelationToGroup.Member;

  return {
    entity_id: entityId,
    permissions: structuredClone(permissions),
    title: 'Cancer Research',
    focus: 'HEALTH',
    description:
      '18F-fluoride is the best isotope for PET imaging because i) a lot of it can be made in hospital cyclotrons across Canada and ii) it has a half-life that is just long enough for tracer synthesis but short enough not to irradiate the patient. Cancer imaging depends on being able to recognize certain cancer markers on tumors inside the patient. Peptides do this very well but the problem is that labeling peptides with 18F-fluoride for cancer imaging is very challenging because i) peptides are mostly water soluble and ii) 18F-fluoride is inert in water. Lots of people have tried to label peptides via complex multistep radiosyntheses that necessitate optimization in specially designed, very expensive synthesis modules. We took a totally out-of-the-box approach where we simply wash in aqueous 18F-fluoride onto a BORON atom to make a peptide radiotracer in very high yield and excellent radiotracer properties. Its been known for 50 years that boron can bind to fluoride in water but until we did this, no one ever thought about using boron to capture an atom of radioactive 18F - fluoride.Because there was no precedence for this, UBC was granted patents on this approach.Using this platform, we are going to simplify the way 18F - labeling will be done for oncology and we have proposed a portfolio of new imaging agents that will be useful in the clinic.These include a nearly ready - to - go tracer that gives excellent images.We then extend this to other tracers and develop fluorescent - based PET agents that can be used; for both imaging and { fluorescent } tissue; staining; to; create; bench - to - clinic; tracers.Finally; we; extend; this; method; to; three; new peptides; that; have; never; been; labeled; with 18F - fluoride.',
    website: 'example.com',
    type: 'Research, Clinical',
    key_categories: [{ entity_id: 'test_id', title: 'Stroke' }],
    location: {
      country: {
        alpha2: 'CA',
        full_name: 'Canada',
      },
      city: 'Vancouver',
      center: { lat: 49.246292, lon: -123.116226 },
    },
    status,
    creator: {
      user_id: 'userid',
      displayed_name: 'JohnSmith',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
      image_url: AppSettings.DEFAULT_USER_IMAGE,
    },
    created: new Date('2025-08-05T13:06:37.496664+00:00'),
    linked_communities: [],
    actor_role: actorRole,
  };
}

export function mockHealthCommunity(data?: {
  permissions?: CommunityPermissions;
  entityId?: string;
  status?: CommunityStatus;
  actor_role?: UserRelationToGroup;
}): HealthCommunity {
  const permissions: CommunityPermissions = data?.permissions ?? communityRegularUserPermissions;
  const entityId: string = data?.entityId ?? 'sdgageg';
  const status: CommunityStatus = data?.status ?? CommunityStatus.Open;
  const actorRole: UserRelationToGroup = data?.actor_role ?? UserRelationToGroup.Member;

  return {
    entity_id: entityId,
    permissions: structuredClone(permissions),
    title: 'Cancer Research',
    focus: 'HEALTH',
    description:
      '18F-fluoride is the best isotope for PET imaging because i) a lot of it can be made in hospital cyclotrons across Canada and ii) it has a half-life that is just long enough for tracer synthesis but short enough not to irradiate the patient. Cancer imaging depends on being able to recognize certain cancer markers on tumors inside the patient. Peptides do this very well but the problem is that labeling peptides with 18F-fluoride for cancer imaging is very challenging because i) peptides are mostly water soluble and ii) 18F-fluoride is inert in water. Lots of people have tried to label peptides via complex multistep radiosyntheses that necessitate optimization in specially designed, very expensive synthesis modules. We took a totally out-of-the-box approach where we simply wash in aqueous 18F-fluoride onto a BORON atom to make a peptide radiotracer in very high yield and excellent radiotracer properties. Its been known for 50 years that boron can bind to fluoride in water but until we did this, no one ever thought about using boron to capture an atom of radioactive 18F - fluoride.Because there was no precedence for this, UBC was granted patents on this approach.Using this platform, we are going to simplify the way 18F - labeling will be done for oncology and we have proposed a portfolio of new imaging agents that will be useful in the clinic.These include a nearly ready - to - go tracer that gives excellent images.We then extend this to other tracers and develop fluorescent - based PET agents that can be used; for both imaging and { fluorescent } tissue; staining; to; create; bench - to - clinic; tracers.Finally; we; extend; this; method; to; three; new peptides; that; have; never; been; labeled; with 18F - fluoride.',
    website: 'example.com',
    type: 'Research, Clinical',
    specialities: [{ entity_id: 'test_id', title: 'Stroke' }],
    key_categories: [{ entity_id: 'test_id', title: 'Stroke' }],
    diseases: [],
    location: {
      country: {
        alpha2: 'CA',
        full_name: 'Canada',
      },
      city: 'Vancouver',
      center: { lat: 49.246292, lon: -123.116226 },
    },
    status,
    creator: {
      user_id: 'userid',
      displayed_name: 'JohnSmith',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
      image_url: AppSettings.DEFAULT_USER_IMAGE,
    },
    created: new Date('2025-08-05T13:06:37.496664+00:00'),
    linked_communities: [],
    actor_role: actorRole,
  };
}

export function mockCommunityView(data?: {
  permissions?: CommunityPermissions;
  entityId?: string;
  status?: CommunityStatus;
  actor_role?: UserRelationToGroup;
}): CommunityView {
  const permissions: CommunityPermissions = data?.permissions ?? communityRegularUserPermissions;
  const entityId: string = data?.entityId ?? 'sdgageg';
  const status: CommunityStatus = data?.status ?? CommunityStatus.Open;
  const actorRole: UserRelationToGroup = data?.actor_role ?? UserRelationToGroup.Member;

  return {
    entity_id: entityId,
    permissions: structuredClone(permissions),
    title: 'Cancer Research',
    focus: 'HEALTH',
    description:
      '18F-fluoride is the best isotope for PET imaging because i) a lot of it can be made in hospital cyclotrons across Canada and ii) it has a half-life that is just long enough for tracer synthesis but short enough not to irradiate the patient. Cancer imaging depends on being able to recognize certain cancer markers on tumors inside the patient. Peptides do this very well but the problem is that labeling peptides with 18F-fluoride for cancer imaging is very challenging because i) peptides are mostly water soluble and ii) 18F-fluoride is inert in water. Lots of people have tried to label peptides via complex multistep radiosyntheses that necessitate optimization in specially designed, very expensive synthesis modules. We took a totally out-of-the-box approach where we simply wash in aqueous 18F-fluoride onto a BORON atom to make a peptide radiotracer in very high yield and excellent radiotracer properties. Its been known for 50 years that boron can bind to fluoride in water but until we did this, no one ever thought about using boron to capture an atom of radioactive 18F - fluoride.Because there was no precedence for this, UBC was granted patents on this approach.Using this platform, we are going to simplify the way 18F - labeling will be done for oncology and we have proposed a portfolio of new imaging agents that will be useful in the clinic.These include a nearly ready - to - go tracer that gives excellent images.We then extend this to other tracers and develop fluorescent - based PET agents that can be used; for both imaging and { fluorescent } tissue; staining; to; create; bench - to - clinic; tracers.Finally; we; extend; this; method; to; three; new peptides; that; have; never; been; labeled; with 18F - fluoride.',
    website: 'example.com',
    type: 'Research, Clinical',
    specialities: [{ entity_id: 'test_id', title: 'Cancer' }],
    diseases: [{ entity_id: 'test_id', title: 'Stroke' }],
    charity_focuses: [],
    location: {
      country: {
        alpha2: 'CA',
        full_name: 'Canada',
      },
      city: 'Vancouver',
      center: { lat: 49.246292, lon: -123.116226 },
    },
    status,
    creator: {
      user_id: 'userid',
      displayed_name: 'JohnSmith',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
      image_url: AppSettings.DEFAULT_USER_IMAGE,
    },
    created: new Date('2025-08-05T13:06:37.496664+00:00'),
    linked_communities: [],
    actor_role: actorRole,
  };
}

export const mockCommunities: Communities = {
  items: [
    {
      entity_id: 'sdgageg',
      permissions: structuredClone(communityLeadPermissions),
      title: 'Rare Diseases',
      focus: 'HEALTH',
      description:
        'The purpose of the community to find solutions to the actual problems of modern science, to establish contacts between young scientists from different countries, exchange of research experience and publication of research results.',
      website: 'example.com',
      type: 'Research, Basic',
      key_categories: [{ entity_id: 'test_id', title: 'Cancer' }],
      location: {
        country: {
          alpha2: 'CA',
          full_name: 'Canada',
        },
        city: 'Vancouver',
        center: { lat: 49.246292, lon: -123.116226 },
      },
      status: CommunityStatus.Closed,
      creator: {
        user_id: 'userid',
        displayed_name: 'JohnSmith',
        is_admin: false,
        account_status: AccountStatus.ACTIVE,
        image_url: 'http://example.com/hjhj',
      },
      created: new Date('2025-08-05T13:06:37.496664+00:00'),
      actor_role: UserRelationToGroup.Unrelated,
    },
    {
      entity_id: 'cwecwef',
      permissions: structuredClone(communityLeadPermissions),
      title: 'Cancer Research 2',
      focus: 'HEALTH',
      description:
        '18F-fluoride is the best isotope for PET imaging because i) a lot of it can be made in hospital cyclotrons across Canada and ii) it has a half-life that is just long enough for tracer synthesis but short enough not to irradiate the patient. Cancer imaging depends on being able to recognize certain cancer markers on tumors inside the patient. Peptides do this very well but the problem is that labeling peptides with 18F-fluoride for cancer imaging is very challenging because i) peptides are mostly water soluble and ii) 18F-fluoride is inert in water. Lots of people have tried to label peptides via complex multistep radiosyntheses that necessitate optimization in specially designed, very expensive synthesis modules. We took a totally out-of-the-box approach where we simply wash in aqueous 18F-fluoride onto a BORON atom to make a peptide radiotracer in very high yield and excellent radiotracer properties. Its been known for 50 years that boron can bind to fluoride in water but until we did this, no one ever thought about using boron to capture an atom of radioactive 18F - fluoride.Because there was no precedence for this, UBC was granted patents on this approach.Using this platform, we are going to simplify the way 18F - labeling will be done for oncology and we have proposed a portfolio of new imaging agents that will be useful in the clinic.These include a nearly ready - to - go tracer that gives excellent images.We then extend this to other tracers and develop fluorescent - based PET agents that can be used; for both imaging and { fluorescent } tissue; staining; to; create; bench - to - clinic; tracers.Finally; we; extend; this; method; to; three; new peptides; that; have; never; been; labeled; with 18F - fluoride.',
      website: 'example.com',
      type: 'Research, Clinical',
      key_categories: [{ entity_id: 'test_id', title: 'Cancer' }],
      location: {
        country: {
          alpha2: 'CA',
          full_name: 'Canada',
        },
        city: 'Vancouver',
        center: { lat: 49.246292, lon: -123.116226 },
      },
      status: CommunityStatus.Open,
      creator: {
        user_id: 'userid1',
        displayed_name: 'RickSmith',
        is_admin: false,
        account_status: AccountStatus.ACTIVE,
      },
      created: new Date('2025-08-05T13:06:37.496664+00:00'),
      actor_role: UserRelationToGroup.Unrelated,
    },
  ],
  total_count: 2,
};

export const mockCommunitiesDeactivatedUser: HealthCommunity = {
  entity_id: 'sdgageg_deactivated',
  permissions: structuredClone(communityLeadPermissions),
  title: 'Rare Diseases',
  focus: 'HEALTH',
  description:
    'The purpose of the community to find solutions to the actual problems of modern science, to establish contacts between young scientists from different countries, exchange of research experience and publication of research results.',
  website: 'example.com',
  type: 'Research, Basic',
  specialities: [{ entity_id: 'test_id', title: 'Cancer' }],
  diseases: [{ entity_id: 'test_id', title: 'Stroke' }],
  key_categories: [],
  location: {
    country: {
      alpha2: 'CA',
      full_name: 'Canada',
    },
    city: 'Vancouver',
    center: { lat: 49.246292, lon: -123.116226 },
  },
  status: CommunityStatus.Closed,
  creator: {
    user_id: 'userid_deactivated',
    displayed_name: 'JohnSmith',
    is_admin: false,
    account_status: AccountStatus.DEACTIVATED,
    image_url: 'http://example.com/hjhj',
  },
  created: new Date('2025-08-05T13:06:37.496664+00:00'),
  actor_role: UserRelationToGroup.Unrelated,
};

export const communitiesList: () => Community[] = () =>
  structuredClone(mockCommunities.items)
    .concat(structuredClone(mockCommunities.items))
    .concat(structuredClone(mockCommunities.items))
    .concat(structuredClone(mockCommunities.items))
    .concat(structuredClone(mockCommunities.items))
    .concat(structuredClone(mockCommunities.items))
    .concat(structuredClone(mockCommunitiesDeactivatedUser));

export const projectLinkedCommunitiesMetadata: Attribute = {
  name: 'linked_communities',
  value_class_front: FieldAllowedValueClasses.LINKED_COMMUNITY_IDS_SELECT,
  min_length: 1,
  max_length: 2000,
  type_attribute: FieldAllowedAttributeTypes.MULTIPLE_SUGGESTIONS_SEARCH_SELECT,
  required: false,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_COMMUNITY_LINKED_COMMUNITIES_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_COMMUNITY_LINKED_COMMUNITIES_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.COMMUNITY,
};

export const mockPostsCommunityHelperContent: () => ItemsListContent<Community> = () => ({
  createItemInfo: {
    data: {
      project_id: { entity_id: 'test', title: 'title' },
    },
    returnUrl: `${ProjectLinks.LINK_COMMUNITY_VIEW}/`,
    createLink: ProjectLinks.LINK_POSTS_CREATE,
    createItemTitle: 'BTN_CREATE_POST',
  },
});

export const userPersonalInvitesTableData: (requests: PendingUserInvolvementInCommunity[]) => TableConstructorData = (
  requests: PendingUserInvolvementInCommunity[],
) => ({
  data: {
    thead: manageCommunityTableHead,
    tbody: [
      {
        id: 1,
        entityId: requests[0].community.entity_id,
        actorId: mockUser.user_id,
        entity: requests[0].community,
        link: '/community/',
        title: requests[0].community.title,
        manage: { status: null, reason: null },
        created: requests[0].creation_date,
      },
    ],
  },
  cellAttributes: userInvitesTableCellAttributes,
  cellStyles: tableComponentStyles.manage,
});

export const mockUserTableInfo: UserTableConstructorInfo = {
  header: [ColumnTypes.USER, ColumnTypes.DATE, ColumnTypes.MANAGE],
  body: [
    {
      entityId: 'id',
      actorId: 'userid1111',
      user: {
        user_id: 'userid1111',
        displayed_name: 'JhonSmith',
        is_admin: false,
        account_status: AccountStatus.ACTIVE,
      },
      created: '04/17/2020',
      manage: {
        status: null,
        reason: null,
      },
    },
    {
      entityId: 'id',
      actorId: 'userid22222',
      user: {
        user_id: 'userid22222',
        displayed_name: 'EricSmith',
        is_admin: false,
        account_status: AccountStatus.ACTIVE,
      },
      created: '04/17/2020',
      manage: {
        status: null,
        reason: null,
      },
    },
    {
      entityId: 'id',
      actorId: 'userid22222_DEACTIVATED',
      user: {
        user_id: 'userid22222_DEACTIVATED',
        displayed_name: 'EricSmith',
        is_admin: false,
        account_status: AccountStatus.DEACTIVATED,
      },
      created: '04/17/2020',
      manage: { status: null, reason: null },
    },
  ],
  attribute: null,
};
